import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { path: '/', label: 'Ana Sayfa', id: 'home' },
    { path: '/products', label: 'Ürünler', id: 'products' },
    { path: '/services', label: 'Hizmetler', id: 'services' },
    { path: '/references', label: 'Referanslar', id: 'references' },
    { path: '/news', label: 'Haberler', id: 'news' },
    { path: '/gallery', label: 'Galeri', id: 'gallery' },
    { path: '/documents', label: 'Belgelerimiz', id: 'documents' },
    { path: '/about', label: 'Hakkımızda', id: 'about' },
    { path: '/contact', label: 'İletişim', id: 'contact' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-lg fixed w-full top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Link to="/" className="flex items-center hover:opacity-80 transition duration-300">
                <img 
                  src="/logo-onur.png" 
                  alt="Onur İnşaat Logo" 
                  className="h-16 w-auto"
                  onError={(e) => {
                    // Fallback if logo doesn't exist
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden items-center">
                  <div className="bg-brand-orange w-10 h-10 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-xl">O</span>
                  </div>
                  <span className="text-2xl font-bold text-brand-navy">Onur Beton</span>
                </div>
              </Link>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              {navigationItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`font-medium transition duration-300 ${
                    location.pathname === item.path 
                      ? 'text-brand-orange border-b-2 border-brand-orange' 
                      : 'text-brand-gray hover:text-brand-orange'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            
            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-brand-gray"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
          
          {/* Mobile Navigation */}
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="md:hidden pb-4"
            >
              <nav className="flex flex-col space-y-3">
                {navigationItems.map((item) => (
                  <Link
                    key={item.id}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`text-left py-2 px-4 rounded-lg transition duration-300 ${
                      location.pathname === item.path 
                        ? 'bg-orange-100 text-brand-orange' 
                        : 'text-brand-gray hover:bg-gray-100'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </motion.div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-brand-navy text-white py-12 mt-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="/logo-onur.png" 
                  alt="Onur İnşaat Logo" 
                  className="h-12 w-auto mr-3"
                  onError={(e) => {
                    // Fallback if logo doesn't exist
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden items-center">
                  <div className="bg-brand-orange w-8 h-8 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold">O</span>
                  </div>
                  <h3 className="text-xl font-bold">Onur Beton</h3>
                </div>
              </div>
              <p className="text-gray-300">
                Kalite ve güven ilkesiyle hizmet veren hazır beton üreticisi. 
                Sektördeki tecrübemizle projelerinize değer katıyoruz.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Hızlı Linkler</h4>
              <ul className="space-y-2">
                {[
                  { path: '/', label: 'Ana Sayfa' },
                  { path: '/products', label: 'Ürünler' },
                  { path: '/services', label: 'Hizmetler' },
                  { path: '/contact', label: 'İletişim' }
                ].map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="text-gray-300 hover:text-brand-orange transition duration-300"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">İletişim</h4>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-3 text-brand-orange" />
                  <span>(0356) 527 86 65</span>
                </div>
                <div className="flex items-center">
                  <Mail className="w-5 h-5 mr-3 text-brand-orange" />
                  <span>info@onurbeton.com.tr</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-3 text-brand-orange" />
                  <span>Niksar/Tokat, Türkiye</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Onur Beton. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout; 